import { useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { CheckCircle } from "lucide-react";
import { DASHBOARD } from "../../routes/routes";
import ApiCollections from "../../configs/services/apiCollections";

// Styled Components
const Container = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem;
`;

const SuccessCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  max-width: 500px;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    max-width: 100%;
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
  animation: scaleIn 0.5s ease-out;

  @keyframes scaleIn {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  svg {
    color: white;
    width: 48px;
    height: 48px;
  }
`;

const Title = styled.h1`
  color: #1f2937;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Message = styled.p`
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
`;

const DashboardLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #8b5cf6, #7c3aed);
  color: white !important;
  border: none;
  padding: 0.875rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;
  box-shadow: 0 10px 20px rgba(139, 92, 246, 0.32), inset 0 1px 0 rgba(255,255,255,0.35);

  &:link,
  &:visited,
  &:hover,
  &:active {
    color: white !important;
  }

  &:hover {
    background: linear-gradient(180deg, #7c3aed, #6d28d9);
    transform: translateY(-2px);
    box-shadow: 0 14px 26px rgba(109, 40, 217, 0.36);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.95rem;
    width: 100%;
  }
`;

function AuthSuccess() {
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        // Fetch fulfillment policies
        const fulfillmentResponse = await ApiCollections.getFulfillmentPolicies({
          marketplace_id: "EBAY_DE",
        });
        console.log("Fulfillment Policies:", fulfillmentResponse);

        // Fetch payment policies
        const paymentResponse = await ApiCollections.getPaymentPolicies({
          marketplace_id: "EBAY_DE",
        });
        console.log("Payment Policies:", paymentResponse);

        // Fetch return policies
        const returnResponse = await ApiCollections.getReturnPolicies({
          marketplace_id: "EBAY_DE",
        });
        console.log("Return Policies:", returnResponse);
      } catch (error) {
        console.error("Error fetching policies:", error);
      }
    };

    fetchPolicies();
  }, []);

  return (
    <Container>
      <SuccessCard>
        <SuccessIcon>
          <CheckCircle />
        </SuccessIcon>
        <Title>Authorization Successful</Title>
        <Message>
          Your authorization has been completed successfully. You can now access your dashboard.
        </Message>
        <DashboardLink to={DASHBOARD}>
          Go to Dashboard
        </DashboardLink>
      </SuccessCard>
    </Container>
  );
}

export default AuthSuccess;

